const ENV_MAPPING = {
  production: 'live',
  livetest: 'test',
  uat: 'test',
  test: 'test',
  local: 'test',
};

function detectEnvironment() {
  if (typeof window === 'undefined') return 'test';
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'test';
  // subdomain-prefix forms: test.shopeepay.co.id, uat.shopeepay.com.my, staging.shopeepay.sg
  if (/^(test|uat|staging)\./.test(hostname)) return 'test';
  // infix forms used by Shopee internal hosts: *.test.shopee.io, *-test-*.shopeemobile.com
  if (hostname.includes('.test.') || hostname.includes('-test.')) return 'test';
  if (hostname.includes('.uat.') || hostname.includes('-uat.')) return 'test';
  return 'live';
}

function toBase64(str) {
  try {
    return btoa(str);
  } catch {
    return str;
  }
}

function toStringLabels(labels) {
  if (!labels) return undefined;
  const result = {};
  for (const key of Object.keys(labels)) {
    result[key] = String(labels[key]);
  }
  return result;
}

class Reporter {
  constructor(config) {
    this._metrics = [];
    this._timer = null;
    this._maxBatch = 100;
    this._flushInterval = 1000;

    const env = config.environment
      ? (ENV_MAPPING[config.environment] || config.environment)
      : detectEnvironment();

    const hostname =
      env === 'live' ? 'api.fe.shopeepay.sg' : 'api.fe.test.shopeepay.sg';
    this._url = `https://${hostname}/api/v3/telemetry`;

    this._baseInfo = {
      region: config.region,
      environment: env,
      platform: 'web',
      project: config.project,
      app_type: config.appType || 'shopeepay-website',
      app_version:
        typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev',
    };

    if (config.enabled !== undefined) {
      this._enabled = config.enabled;
    } else {
      // Default-disable in SSR (no `window`) and on localhost. Without this,
      // SSR runs would queue metrics and try to call sendBeacon on every flush,
      // crashing or silently dropping data.
      const isSSR = typeof window === 'undefined';
      const isLocalhost =
        !isSSR &&
        (window.location.hostname === 'localhost' ||
          window.location.hostname === '127.0.0.1');
      this._enabled = !(isSSR || isLocalhost);
    }

    // Force a synchronous flush when the page is about to be unloaded.
    // Without this, the 1-second debounce in _scheduleFlush() drops every
    // metric queued in the last second of the visit — which is exactly when
    // the most valuable signals (web vitals' visibilitychange flush, last
    // error before close) get queued. sendBeacon is designed for this
    // unload-time send and works even when the tab is closing.
    //
    // Both events are needed: `visibilitychange:hidden` covers tab switching
    // and mobile background; `pagehide` covers actual unload. They can fire
    // in either order, so the reporter clears its queue on whichever fires
    // first — the second is a no-op.
    if (typeof window !== 'undefined' && this._enabled) {
      const flushOnHide = () => {
        if (this._metrics.length > 0) this._flush();
      };
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushOnHide();
      });
      window.addEventListener('pagehide', flushOnHide);
    }
  }

  getConfig() {
    return {
      region: this._baseInfo.region,
      project: this._baseInfo.project,
      environment: this._baseInfo.environment,
    };
  }

  setEnabled(enabled) {
    this._enabled = enabled;
  }

  addMetric(metric) {
    if (!this._enabled) return;
    this._metrics.push({
      name: metric.name,
      value: metric.value,
      labels: toStringLabels(metric.labels),
    });
    this._scheduleFlush();
  }

  addMetrics(metrics) {
    if (!this._enabled) return;
    for (const m of metrics) {
      this._metrics.push({
        name: m.name,
        value: m.value,
        labels: toStringLabels(m.labels),
      });
    }
    this._scheduleFlush();
  }

  _scheduleFlush() {
    if (this._timer) clearTimeout(this._timer);

    if (this._metrics.length >= this._maxBatch) {
      this._flush();
    } else {
      this._timer = setTimeout(() => this._flush(), this._flushInterval);
    }
  }

  _flush() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }

    const metrics = this._metrics;
    this._metrics = [];

    if (metrics.length === 0) return;

    const timestamp = Date.now();
    const sign = toBase64(`${timestamp}${this._url}`);

    const body = JSON.stringify({
      base_info: this._baseInfo,
      metrics,
      sign,
      sign_timestamp: timestamp,
    });

    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'text/plain' });
        navigator.sendBeacon(this._url, blob);
      } else {
        fetch(this._url, {
          method: 'POST',
          body,
          headers: { 'Content-Type': 'text/plain' },
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // silently fail
    }
  }
}

export default Reporter;
